/*
 * Blooms & Beans — gift builder (sections/bnb-builder.liquid).
 * Staged flow: flowers (required) -> cake (optional) -> balloons (optional) ->
 * delivery details. The canvas shows the actual selected products, line prices
 * and the merchandise subtotal. "Add gift to cart" sends every selected real
 * variant in ONE /cart/add.js request with the product page's delivery
 * properties; the message card goes on the flowers line only. After a failure
 * the server cart is re-read and the customer is told exactly what is in it.
 * The existing cart is never cleared.
 */
import { DeliveryDetails } from '@bnb/delivery';
import { addItems, formatMoney } from '@bnb/cart';

const T = (() => { try { return JSON.parse(document.querySelector('[data-bnb-builder-i18n]')?.textContent || '{}'); } catch { return {}; } })();
const KINDS = ['flowers', 'cake', 'balloons'];

function init(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = '1';
  const panels = [...root.querySelectorAll('[data-panel]')];
  const tabs = [...root.querySelectorAll('[data-goto]')];
  const lines = root.querySelector('[data-lines]');
  const total = root.querySelector('[data-total]');
  const addBtn = root.querySelector('[data-add]');
  const status = root.querySelector('[data-status]');
  const empty = root.querySelector('[data-empty]');
  const details = new DeliveryDetails(root.querySelector('[data-delivery]'));
  let current = 0, busy = false, lastAddedKey = '';

  const selected = (kind) => root.querySelector(`input[data-kind="${kind}"]:checked`);

  function go(i, focus = true) {
    if (i > 0 && !selected('flowers')) {
      i = 0;
      panels[0].querySelector('[data-step-error]').textContent = T.required || 'Choose your flowers to continue.';
    }
    current = i;
    panels.forEach((p, k) => { p.hidden = k !== i; });
    tabs.forEach((t, k) => { if (k === i) t.setAttribute('aria-current', 'step'); else t.removeAttribute('aria-current'); });
    if (focus) {
      const target = panels[i].querySelector('input:checked') || panels[i].querySelector('input, select, button');
      target?.focus({ preventScroll: true });
      if (window.matchMedia('(max-width: 989px)').matches) panels[i].scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  function key() {
    return KINDS.map((k) => selected(k)?.value || '').join('|');
  }

  function render() {
    lines.innerHTML = '';
    let sum = 0, any = false;
    KINDS.forEach((kind) => {
      const input = selected(kind);
      const fig = root.querySelector(`[data-slot="${kind}"]`);
      if (!input) { fig.hidden = true; return; }
      any = true;
      sum += Number(input.dataset.price);
      fig.hidden = false;
      const img = fig.querySelector('img');
      if (img.getAttribute('src') !== input.dataset.img) img.src = input.dataset.img;
      const li = document.createElement('li');
      const name = document.createElement('span');
      name.innerHTML = `<span class="bnb-canvas__kind"></span>`;
      name.firstChild.textContent = (T.kinds && T.kinds[kind]) || kind;
      const a = document.createElement('a'); a.href = input.dataset.url; a.textContent = input.dataset.title; a.style.color = 'inherit';
      name.append(a);
      const price = document.createElement('span'); price.textContent = formatMoney(Number(input.dataset.price));
      li.append(name, price);
      if (kind !== 'flowers') {
        const rm = document.createElement('button'); rm.type = 'button';
        rm.textContent = '×'; rm.setAttribute('aria-label', (T.remove || 'Remove %s').replace('%s', input.dataset.title));
        rm.addEventListener('click', () => { input.checked = false; render(); });
        li.append(rm);
      } else {
        li.append(document.createElement('span'));
      }
      lines.append(li);
    });
    empty.hidden = any;
    total.textContent = formatMoney(sum);
    // Guard against accidental duplicates: after a successful add, the same selection can't be re-sent.
    const same = lastAddedKey && key() === lastAddedKey;
    addBtn.disabled = busy || !selected('flowers') || same;
    tabs.forEach((t, k) => { t.querySelector('.bnb-tick')?.remove(); if (k < 3 && selected(KINDS[k])) { const s = document.createElement('span'); s.className = 'bnb-tick'; s.setAttribute('aria-hidden', 'true'); s.textContent = '✓'; t.append(s); } });
  }

  root.addEventListener('change', (e) => {
    if (e.target.matches('input[data-kind]')) {
      panels[current].querySelector('[data-step-error]') && (panels[current].querySelector('[data-step-error]').textContent = '');
      status.textContent = ''; status.className = 'bnb-status';
      render();
    }
  });
  tabs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.goto))));
  root.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => go(Math.min(current + 1, panels.length - 1))));
  root.querySelectorAll('[data-prev]').forEach((b) => b.addEventListener('click', () => go(Math.max(current - 1, 0))));
  root.querySelectorAll('[data-skip]').forEach((b) => b.addEventListener('click', () => {
    const kind = KINDS[current];
    if (kind && kind !== 'flowers') { const s = selected(kind); if (s) s.checked = false; render(); }
    go(current + 1);
  }));

  addBtn.addEventListener('click', async () => {
    if (busy) return;
    if (!selected('flowers')) { go(0); return; }
    if (current !== 3) { go(3); if (!details.validate()) return; }
    else if (!details.validate()) return;
    const base = details.properties({ includeMessage: false });
    const withMessage = details.properties({ includeMessage: true });
    const items = KINDS.map((k) => selected(k)).filter(Boolean).map((input) => ({
      id: Number(input.value), quantity: 1, properties: input.dataset.kind === 'flowers' ? withMessage : base,
    }));
    busy = true; render();
    addBtn.setAttribute('aria-busy', 'true'); addBtn.textContent = T.adding || 'Adding…';
    status.className = 'bnb-status'; status.textContent = '';
    const res = await addItems(items, 'bnb-builder');
    busy = false; addBtn.removeAttribute('aria-busy'); addBtn.textContent = T.addAll || 'Add gift to cart';
    if (res.ok) {
      lastAddedKey = key();
      status.className = 'bnb-status bnb-status--ok';
      status.textContent = T.success || 'Your gift has been added to the cart.';
    } else if (res.addedTitles.length) {
      lastAddedKey = key();
      status.className = 'bnb-status bnb-status--error';
      status.textContent = (T.partial || 'Only some items were added: %s').replace('%s', res.addedTitles.join(', '));
    } else {
      status.className = 'bnb-status bnb-status--error';
      status.textContent = (T.noneAdded || 'Nothing was added to your cart. %s').replace('%s', res.message || '');
    }
    render();
  });

  go(0, false);
  render();
}

document.querySelectorAll('[data-bnb-builder]').forEach(init);
document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-bnb-builder]').forEach(init));
